import { useMemo, useState } from "react";
import { ScrollView, View } from "react-native";
import { Check, FlaskConical, Plus, Snowflake } from "lucide-react-native";
import {
  ActionRowCard,
  Dialog,
  DialogHeader,
  ListRowSkeleton,
} from "@louise/moon-ui";
import { useTranslations, type Locale } from "@src/packages/i18n";
import { useLocale } from "@src/packages/i18n/useLocale";
import { formatDate } from "@src/packages/i18n/formatters";
import {
  useSpecimens,
  type Specimen,
} from "@src/packages/care/cryovault/useSpecimens";
import { resolveIuiSpecimenLinkage } from "@src/packages/care/iui/resolveIuiSpecimenLinkage";
import { EMBER } from "../../../../../onboarding/theme";
import { semanticColors } from "@louise/theme/tokens";

type Props = {
  cycleId: string;
  linkedSpecimenId: string | null;
  onLinkSpecimen: (specimen: Specimen) => void;
  onOpenAddSpecimen: () => void;
};

/**
 * A sample identity that is a machine key rather than a name.
 *
 * `resolveSemenSampleIds` mints one `createIdempotencyKey()` per collection so
 * the vials of one draw stay grouped — its own module calls it "one stable sample
 * identity per semen collection". It is a grouping key, and it was being shown to
 * the patient as her sample's name: a bare
 * `431c7f88-5cc7-4649-b2a3-0011df45c864` on the card.
 *
 * The test is deliberately on shape, not provenance. `specimen.source` would say
 * who stamped the id, but it is the wrong question twice over: `sampleId` is
 * patient-editable, and a clinic LIMS may well use UUIDs as accession keys. A
 * thirty-six character UUID is not a name whoever minted it, and a short human
 * reference is worth showing whoever did — which is exactly what shape answers.
 *
 * The deeper oddity is upstream: this app puts an idempotency UUID into a
 * patient-facing field at all. Changing that changes how vials are grouped, so
 * it is not this presentational fix's business.
 */
const GENERATED_SAMPLE_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function specimenLabel(
  specimen: Specimen,
  t: ReturnType<typeof useTranslations>,
  locale: Locale,
): string {
  if (specimen.sampleId && !GENERATED_SAMPLE_ID.test(specimen.sampleId)) {
    return specimen.sampleId;
  }
  // The date is what actually tells two samples apart for her, and it is the
  // thing she entered herself.
  //
  // Through the shared `formatDate`, which is the only correct way to render one
  // of these: `collectedAt` is a bare `YYYY-MM-DD`, and `new Date()` parses that
  // as UTC midnight — so a raw `Intl.DateTimeFormat` showed a New York patient
  // the day before ("30 juin" for a sample collected 1 July). `coerceDate` pins
  // it to local midnight, and the helper maps the app locale to a real BCP-47
  // tag rather than passing "fr" through.
  if (specimen.collectedAt) {
    const date = formatDate(specimen.collectedAt, locale, {
      day: "numeric",
      month: "long",
    });
    if (date) {
      return t("care.treatment_hub.iui_specimen_linkage.sample_of_date", {
        params: { date },
      });
    }
  }
  // Nothing dated it and nothing named it. Six characters is enough to pick one
  // row out of a short list, where thirty-six was just noise.
  return `…${specimen.id.slice(-6)}`;
}

function specimenMeta(
  specimen: Specimen,
  t: ReturnType<typeof useTranslations>,
): string {
  const origin = specimen.origin
    ? t(`care.treatment_hub.iui_specimen_linkage.origin.${specimen.origin}`)
    : null;
  const preparation = specimen.processingMethod
    ? t(
        `care.treatment_hub.iui_specimen_linkage.processing.${specimen.processingMethod}`,
      )
    : null;
  const status = t(
    `care.treatment_hub.iui_specimen_linkage.status.${specimen.status}`,
  );
  return [origin, status, preparation].filter(Boolean).join(" · ");
}

export function IuiSpecimenLinkage({
  cycleId,
  linkedSpecimenId,
  onLinkSpecimen,
  onOpenAddSpecimen,
}: Props) {
  const t = useTranslations();
  const { locale } = useLocale();
  const [selectorVisible, setSelectorVisible] = useState(false);
  const { data: specimens, isLoading } = useSpecimens("semen");
  const linkage = useMemo(
    () =>
      resolveIuiSpecimenLinkage({
        specimens,
        cycleId,
        linkedSpecimenId,
      }),
    [cycleId, linkedSpecimenId, specimens],
  );

  const linkedSpecimen = linkage.linkedSpecimen;
  const rowSubtitle = linkedSpecimen
    ? `${specimenLabel(linkedSpecimen, t, locale)} · ${specimenMeta(linkedSpecimen, t)}`
    : linkage.status === "unavailable"
      ? t("care.treatment_hub.iui_specimen_linkage.sample_unavailable")
      : t("care.treatment_hub.iui_specimen_linkage.link_or_add");

  const handleSelect = (specimen: Specimen) => {
    onLinkSpecimen(specimen);
    setSelectorVisible(false);
  };

  const handleOpenAddSpecimen = () => {
    setSelectorVisible(false);
    onOpenAddSpecimen();
  };

  return (
    <>
      <ActionRowCard
        title={t("care.treatment_hub.iui_specimen_linkage.title")}
        subtitle={rowSubtitle}
        leading={
          linkedSpecimen ? (
            <Check
              size={20}
              color={semanticColors.status.success}
              strokeWidth={2.1}
            />
          ) : (
            <FlaskConical
              size={20}
              color={EMBER.colors.textLight}
              strokeWidth={1.7}
            />
          )
        }
        onPress={() => setSelectorVisible(true)}
        accessibilityLabel={t("care.treatment_hub.iui_specimen_linkage.title")}
        style={{ marginBottom: 14 }}
      />

      {/* Centred, like every other pick-one dialog in the app.
          `layout="bottom"` made this the odd one out: the backdrop stretches its
          child, and on web the backdrop is the browser window, so the panel ran
          the full width of the viewport over a screen sitting in a 520 column —
          floating clear of the bottom edge it was anchored to, with its lower
          corners square because a bottom sheet only rounds the top two. Nothing
          about choosing a sample from a short list wants a sheet. The content and
          the selection behaviour are unchanged.

          `maxHeight` belongs on the container rather than the surface: the
          percentage resolves against the backdrop, which fills the window, while
          the surface's parent is sized by its own content. */}
      <Dialog
        size="wide"
        visible={selectorVisible}
        onClose={() => setSelectorVisible(false)}
        layout="centered"
        closeButton="minimal"
        contentStyle={{ paddingHorizontal: 16 }}
        testID="iui-specimen-selector"
      >
        <DialogHeader
          title={t(
            "care.treatment_hub.iui_specimen_linkage.choose_sample_title",
          )}
          leading={
            <FlaskConical
              size={20}
              color={EMBER.colors.textLight}
              strokeWidth={1.7}
            />
          }
        />

        <ScrollView
          style={{ marginTop: 16 }}
          contentContainerStyle={{ gap: 8, paddingBottom: 8 }}
          showsVerticalScrollIndicator={false}
        >
          {isLoading ? (
            <View testID="iui-specimen-linkage-loading">
              {[0, 1].map((row) => (
                <ListRowSkeleton key={row} showLeading showSubtitle />
              ))}
            </View>
          ) : (
            linkage.availableSpecimens.map((specimen) => (
              <ActionRowCard
                key={specimen.id}
                title={specimenLabel(specimen, t, locale)}
                subtitle={specimenMeta(specimen, t)}
                leading={
                  specimen.processingMethod === "frozen" ||
                  specimen.processingMethod === "thawed" ? (
                    <Snowflake
                      size={19}
                      color={EMBER.colors.textLight}
                      strokeWidth={1.7}
                    />
                  ) : (
                    <FlaskConical
                      size={19}
                      color={EMBER.colors.textLight}
                      strokeWidth={1.7}
                    />
                  )
                }
                trailing={
                  specimen.id === linkedSpecimenId ? (
                    <Check
                      size={18}
                      color={semanticColors.accent.default}
                      strokeWidth={2.1}
                    />
                  ) : undefined
                }
                onPress={() => handleSelect(specimen)}
                accessibilityLabel={specimenLabel(specimen, t, locale)}
              />
            ))
          )}

          <ActionRowCard
            title={t("care.treatment_hub.iui_specimen_linkage.add_new_sample")}
            leading={
              <Plus size={20} color={EMBER.colors.warm} strokeWidth={1.9} />
            }
            onPress={handleOpenAddSpecimen}
            accessibilityLabel={t(
              "care.treatment_hub.iui_specimen_linkage.add_new_sample",
            )}
          />
        </ScrollView>
      </Dialog>
    </>
  );
}
